/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo, useCallback } from "react";
import type {
  TravelDetailsData,
  PlanSelection,
} from "../../types/customer-portal";
import type { ConvertedCurrencyData } from "../../types/forex-conversion";
import BenefitsModal from "./BenefitsModal";
import ForexRateConversion from "./ForexRateConversion";
import "./TravelDetailsSection.css";

import { useProvinces } from "../../hooks/useProvinces";
import { useDestinations } from "../../hooks/useDestinations";
import { useInternationalDestinations } from "../../hooks/useInternationalDestinations";
import { usePlanRates } from "../../hooks/usePlanRates";
import { usePlanSelection } from "../../contexts/PlanSelectionContext";
import { dateUtils } from "../../../../shared/utils/dateUtils";
import { formatWithCommas } from "../../../../shared/utils/numberUtils";
import { useProductPlanStore } from "@/store/useProductPlanStore";
import { addDays, format } from "date-fns";
import { useApplicationFormStore } from "@/store/useApplicationFormStore";
import { TripCategory } from "@/shared/types/applicationTripTypes";

interface TravelDetailsSectionProps {
  travelData: TravelDetailsData;
  planSelection?: PlanSelection;
  onBack: () => void;
  onNext?: () => void;
}

const TravelDetailsSection: React.FC<TravelDetailsSectionProps> = ({
  travelData,
  planSelection, // Accept planSelection prop
  onBack,
  onNext,
}) => {
  const { setSelectedProductPlan } = useProductPlanStore();
  const { formData } = useApplicationFormStore();
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [isBenefitsModalOpen, setIsBenefitsModalOpen] = useState(false);
  const [selectedBenefitsPlanId, setSelectedBenefitsPlanId] =
    useState<string>("");

  // Forex conversion state
  const [isForexModalOpen, setIsForexModalOpen] = useState(false);
  const [selectedEurPlan, setSelectedEurPlan] = useState<
    (typeof planOptions)[0] | null
  >(null);
  const [convertedPlans, setConvertedPlans] = useState<
    Map<
      string,
      {
        amount: number;
        currency: string;
        currencyId: number;
      }
    >
  >(new Map());

  // Custom hooks
  const { destinations: provinces } = useProvinces();
  const { destinations: Destinations } = useDestinations();
  const { destinationMapping: internationalDestinationMapping } =
    useInternationalDestinations();
  const {
    planOptions,
    loading,
    error,
    fetchSingleTripRates,
    fetchMultiTripRates,
    fetchMultiTripInternationalRates,
    clearPlans,
  } = usePlanRates();

  // Plan selection context
  const {
    setSelectedPlan,
    setSelectedPlanId: setContextPlanId,
    setPlanSection,
    setTempData,
    getTempDataValue,
  } = usePlanSelection();

  // Create mapping objects for efficient lookups (only needed for Single Trip)
  const provinceMapping = useMemo(() => {
    const mapping: Record<string, string> = {};
    provinces.forEach((province) => {
      mapping[province.destinationName] = province.destinationCode;
    });
    return mapping;
  }, [provinces]);

  const destinationMapping = useMemo(() => {
    const mapping: Record<string, string> = {};
    Destinations.forEach((destination) => {
      mapping[destination.destinationName] = destination.destinationCode;
    });
    return mapping;
  }, [Destinations]);

  // Sort plans for domestic to show Bronze, Silver, Gold order, and for international multi-trip to show desired sequence
  const sortedPlanOptions = useMemo(() => {
    if (!planOptions.length) return [];

    // Check if this is international multi-trip (Annual Multi-Trip with mixed Asia Oceania and Worldwide Elite plans)
    const isInternationalMultiTrip =
      travelData.tripType === "Annual Multi-Trip" &&
      planOptions.some(
        (plan) =>
          plan.productName.toLowerCase().includes("asia") ||
          plan.productName.toLowerCase().includes("oceania") ||
          plan.productName.toLowerCase().includes("world") ||
          plan.productName.toLowerCase().includes("elite"),
      );

    if (isInternationalMultiTrip) {
      // For international multi-trip plans, sort by desired sequence:
      // 1. Asia Oceania PHP first
      // 2. Asia Oceania USD second
      // 3. Worldwide Elite USD third
      return [...planOptions].sort((a, b) => {
        const getRegionAndCurrency = (plan: (typeof planOptions)[0]) => {
          const productName = plan.productName.toLowerCase();
          const currency = plan.rate.currency.currencyCode;

          const isAsiaOceania =
            productName.includes("asia") || productName.includes("oceania");
          const isWorldwideElite =
            productName.includes("world") || productName.includes("elite");

          if (isAsiaOceania && currency === "PHP") return 1; // Asia Oceania PHP - highest priority
          if (isAsiaOceania && currency === "USD") return 2; // Asia Oceania USD - second priority
          if (isWorldwideElite && currency === "USD") return 3; // Worldwide Elite USD - third priority

          return 4; // Any other plans - lowest priority
        };

        const priorityA = getRegionAndCurrency(a);
        const priorityB = getRegionAndCurrency(b);

        return priorityA - priorityB;
      });
    }

    // Define the desired order for domestic plans
    const planOrder = ["Bronze", "Silver", "Gold"];

    // Sort plans based on the plan name order (for domestic plans)
    return [...planOptions].sort((a, b) => {
      // Extract plan tier from product name (e.g., "Domestic Gold" -> "Gold")
      const getTier = (productName: string) => {
        const name = productName.toLowerCase();
        if (name.includes("bronze")) return "Bronze";
        if (name.includes("silver")) return "Silver";
        if (name.includes("gold")) return "Gold";
        return productName; // Fallback to original name if no tier found
      };

      const tierA = getTier(a.productName);
      const tierB = getTier(b.productName);

      const indexA = planOrder.indexOf(tierA);
      const indexB = planOrder.indexOf(tierB);

      // If both tiers are in our order array, sort by that order
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }

      // If only one tier is in our order array, prioritize it
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;

      // If neither tier is in our order array, sort by price (ascending)
      return a.rate.rateAmount - b.rate.rateAmount;
    });
  }, [planOptions, travelData.tripType]);

  // FIXED: Determine the plan type for API calls
  const getApiPlanType = useCallback(() => {

    if (travelData.tripType === "Annual Multi-Trip") {
      // For Annual Multi-Trip, use the original plan selection from landing page
      if (planSelection?.planType) {

        // Convert landing page plan type to API plan type
        switch (planSelection.planType) {
          case "Individual Plan":
            return "Individual";
          case "Family Plan":
            return "Family";
          case "Group Plan":
            return "Group";
          case "Multi Trip":
            return "Individual"; // Multi Trip defaults to Individual for API calls
          default:
            return "Individual";
        }
      }

      // Fallback: check if planType was set in travel data
      if (travelData.planType === "Multi Trip") {
        return "Individual"; // Multi Trip defaults to Individual for API calls
      }

      return "Individual"; // Final fallback
    }

    // For Single Trip, use the planType from travel data
    return travelData.planType;
  }, [travelData.tripType, travelData.planType, planSelection]);

  // FIXED: Get dsName from planSelection.section with proper case-insensitive matching
  const getDsName = useCallback(() => {
    if (planSelection?.section) {
      const section = planSelection.section.toLowerCase().trim(); // Make case-insensitive
      // Convert section to proper dsName format with case-insensitive matching
      switch (section) {
        case "asia and oceania":
          return "asia_oceania";
        case "worldwideelite":
        case "worldwide elite":
          return "world_wide_elite";
        case "shortsecure":
        case "short secure":
          return "short_secure";
        case "inbound":
          return "world_wide_elite_inbound";
        case "domestic":
        default:
          return "domestic";
      }
    }

    // CRITICAL FIX: For multi-trip with undefined section, don't default to domestic
    // This allows the multi-trip logic to handle international multi-trip properly
    if (travelData.tripType === "Annual Multi-Trip") {
      return undefined; // Let multi-trip logic decide
    }

    return "domestic"; // Default fallback for single trip
  }, [planSelection, travelData.tripType]);

  // Set the plan section in context when component mounts or planSelection changes
  useEffect(() => {
    if (planSelection?.section) {
      setPlanSection(planSelection.section);
    }
  }, [planSelection?.section, setPlanSection]);

  // Clear plans when section changes to prevent stale data
  useEffect(() => {
    clearPlans();
  }, [planSelection?.section, clearPlans]);

  // Fetch plans when component mounts or when relevant travel data changes
  useEffect(() => {
    const planTypeForApi = getApiPlanType();
    const dsName = getDsName();

    if (travelData.tripType === "Single Trip") {
      // Fetch single trip plans

      const endTripFound =
        formData.category === TripCategory.Inbound
          ? true
          : !!travelData.endTrip;
      if (
        planTypeForApi &&
        travelData.startTrip &&
        travelData.destination.length > 0 &&
        endTripFound &&
        travelData.startDate &&
        travelData.endDate &&
        Object.keys(provinceMapping).length > 0 &&
        Object.keys(destinationMapping).length > 0
      ) {
        // Build API parameters
        const apiParams = {
          planType: planTypeForApi,
          startTrip: travelData.startTrip,
          destination: travelData.destination,
          endTrip: travelData.endTrip,
          startDate: travelData.startDate,
          endDate: travelData.endDate,
          dsName: dsName,
          // Include transport parameter for ShortSecure
          ...(dsName === "short_secure" &&
            travelData.transportMode && {
              transport: travelData.transportMode,
            }),
        };

        fetchSingleTripRates(apiParams, provinceMapping, destinationMapping);
      }
    } else if (travelData.tripType === "Annual Multi-Trip") {
      // FIXED: Corrected logic for multi-trip plan fetching
      const dsName = getDsName();

      // CRITICAL FIX: For Multi Trip plans, always check if it's international
      // Check if this is an International Multi Trip based on multiple indicators
      const isInternationalMultiTrip =
        // Check if planSelection indicates Multi Trip
        planSelection?.planType === "Multi Trip" ||
        // Check if dsName is undefined (indicates international multi-trip from TravelInformationSection)
        dsName === undefined ||
        // Check if dsName is specifically international
        dsName === "asia_oceania" ||
        dsName === "world_wide_elite";

      if (isInternationalMultiTrip) {
        // This is International multi-trip - fetch both international sections
        fetchMultiTripInternationalRates();
      } else if (dsName === "domestic") {
        // For domestic multi-trip, fetch domestic plans
        console.log("Fetching domestic multi-trip plans with dsName:", dsName);
        fetchMultiTripRates({
          dsName: dsName,
        });
      } else {
        // For other specific sections (like short_secure), fetch plans for that section only
        fetchMultiTripRates({
          dsName: dsName,
        });
      }
    }
  }, [
    travelData.tripType,
    travelData.planType,
    travelData.startTrip,
    travelData.destination,
    travelData.endTrip,
    travelData.startDate,
    travelData.endDate,
    provinceMapping,
    destinationMapping,
    fetchSingleTripRates,
    fetchMultiTripRates,
    fetchMultiTripInternationalRates,
    getApiPlanType,
    getDsName,
    planSelection,
    clearPlans,
  ]);

  const handlePlanSelect = (planId: string) => {
    const selectedPlan = sortedPlanOptions.find(
      (plan) => plan.productId.toString() === planId,
    );
    if (selectedPlan) {
      setSelectedProductPlan(selectedPlan);
    }

    // Check if the selected plan uses EUR currency, or is a Worldwide Elite -
    // Inbound plan quoted in USD (clients need to know the PHP payment amount)
    const isEurPlan = selectedPlan?.rate.currency.currencyCode === "EUR";
    const isInboundUsdPlan =
      formData.category === TripCategory.Inbound &&
      selectedPlan?.rate.currency.currencyCode === "USD";

    if (isEurPlan || isInboundUsdPlan) {
      setSelectedEurPlan(selectedPlan);
      setIsForexModalOpen(true);
      return;
    }

    // Check if this is a converted plan being clicked again
    const planKey = planId;
    const existingConversion = convertedPlans.get(planKey);

    if (existingConversion && selectedPlan) {
      setSelectedEurPlan(selectedPlan);
      setIsForexModalOpen(true);
      return;
    }

    // Normal plan selection for non-EUR plans
    setSelectedPlanId(planId);

    // Update context with selected plan data
    if (selectedPlan) {
      // For multi-trip international plans, detect section from product name
      if (travelData.tripType === "Annual Multi-Trip") {
        let detectedSection: PlanSelection["section"] = "Domestic";
        const productNameLower = selectedPlan.productName.toLowerCase();

        if (
          productNameLower.includes("asia") ||
          productNameLower.includes("oceania")
        ) {
          detectedSection = "Asia and Oceania";
        } else if (
          productNameLower.includes("world") ||
          productNameLower.includes("elite")
        ) {
          detectedSection = "WorldwideElite";
        }

        // Store the detected section in context
        setPlanSection(detectedSection);
      }

      setSelectedPlan(selectedPlan);
      setContextPlanId(planId);
    }
  };

  // Derived state for button enablement
  const isNextButtonEnabled = selectedPlanId !== null;

  // Handle next button click with validation
  const handleNext = () => {
    if (isNextButtonEnabled && onNext) {
      onNext();
    }
  };

  const handleViewFullBenefits = (planId: string) => {
    setSelectedBenefitsPlanId(planId);
    setIsBenefitsModalOpen(true);
  };

  const handleCloseBenefitsModal = () => {
    setIsBenefitsModalOpen(false);
    setSelectedBenefitsPlanId("");
  };

  const handleRetryFetchPlans = () => {
    const planTypeForApi = getApiPlanType();
    const dsName = getDsName();

    if (travelData.tripType === "Single Trip") {
      // Build API parameters with transport mode for ShortSecure
      const apiParams = {
        planType: planTypeForApi,
        startTrip: travelData.startTrip,
        destination: travelData.destination,
        endTrip: travelData.endTrip,
        startDate: travelData.startDate,
        endDate: travelData.endDate,
        dsName: dsName,
        // Include transport parameter for ShortSecure.
        // "Via Land or Sea" is a single UI option, so both map to transport=land
        ...(dsName === 'short_secure' &&
          (travelData.transportMode === 'land' || travelData.transportMode === 'sea') && {
            transport: 'land' as const
          })
      };

      fetchSingleTripRates(apiParams, provinceMapping, destinationMapping);
    } else if (travelData.tripType === "Annual Multi-Trip") {
      // FIXED: Apply same corrected logic for retry function

      // CRITICAL FIX: Use the same International Multi Trip detection logic as main function
      const isInternationalMultiTrip =
        // Check if planSelection indicates Multi Trip
        planSelection?.planType === "Multi Trip" ||
        // Check if dsName is undefined (indicates international multi-trip from TravelInformationSection)
        dsName === undefined ||
        // Check if dsName is specifically international
        dsName === "asia_oceania" ||
        dsName === "world_wide_elite";

      if (isInternationalMultiTrip) {
        // This is International multi-trip - fetch both international sections
        fetchMultiTripInternationalRates();
      } else if (dsName === "domestic") {
        // For domestic multi-trip, fetch domestic plans
        fetchMultiTripRates({
          dsName: dsName,
        });
      } else {
        // For other specific sections (like short_secure), fetch plans for that section only
        fetchMultiTripRates({
          dsName: dsName,
        });
      }
    }
  };

  // Handle forex conversion completion
  const handleForexConversionComplete = (
    convertedAmount: number,
    convertedCurrency: string,
    currencyId: number,
  ) => {
    if (!selectedEurPlan) return;

    let storedTempDataObj = {};
    const storedTempData = localStorage.getItem("planSelectionTempData");
    try {
      if (storedTempData) {
        storedTempDataObj = JSON.parse(storedTempData);
      }
    } catch (error) {
      console.log("Error parsing stored temp data", error);
    }

    // Store the conversion result locally for UI display
    const planKey = selectedEurPlan.productId.toString();
    setConvertedPlans(
      (prev) =>
        new Map(
          prev.set(planKey, {
            amount: convertedAmount,
            currency: convertedCurrency,
            currencyId,
          }),
        ),
    );

    // CRITICAL FIX: Update tempData with converted currency information
    // Get existing tempData to preserve other fields
    const existingTempData = {
      ...storedTempDataObj,
      productName: getTempDataValue("productName") as string,
      typeName: getTempDataValue("typeName") as string,
      startTrip: getTempDataValue("startTrip") as string,
      destinations: getTempDataValue("destinations") as string | string[],
      endTrip: getTempDataValue("endTrip") as string,
      startDate: getTempDataValue("startDate") as string,
      endDate: getTempDataValue("endDate") as string,
      numberOfTraveler: getTempDataValue("numberOfTraveler") as number,
      numberOfTravelers: getTempDataValue("numberOfTravelers") as number,
      travelPurpose: getTempDataValue("travelPurpose") as string,
      purposeOfTravel: getTempDataValue("purposeOfTravel") as string,
      principal_id: getTempDataValue("principal_id") as number,
      principalId: getTempDataValue("principalId") as number,
      middleName: getTempDataValue("middleName") as string,
      suffix: getTempDataValue("suffix") as string,
      birthDate: getTempDataValue("birthDate") as string,
      dateOfBirth: getTempDataValue("dateOfBirth") as string,
      email: getTempDataValue("email") as string,
      mbl: getTempDataValue("mbl") as string | number,
    };

    // Create enhanced tempData with forex conversion information
    const enhancedTempData: ConvertedCurrencyData & Record<string, unknown> = {
      ...existingTempData,

      // Store original EUR data for reference
      originalCurrencyCode: selectedEurPlan.rate.currency.currencyCode,
      originalRateAmount: selectedEurPlan.rate.rateAmount,
      originalCurrencyId: selectedEurPlan.rate.currency.currencyId,

      // Store converted currency data (required by ConvertedCurrencyData interface)
      convertedCurrencyCode: convertedCurrency,
      convertedRateAmount: convertedAmount,
      convertedCurrencyId: currencyId,

      // Update current currency data to converted values (used for calculations)
      currencyCode: convertedCurrency,
      rateAmount: convertedAmount,
      currencyId: currencyId,

      // Add conversion metadata
      isForexConverted: true,
      conversionTimestamp: new Date().toISOString(),
      conversionRate: convertedAmount / selectedEurPlan.rate.rateAmount,

      // Preserve plan information
      productName: selectedEurPlan.productName,
      typeName: selectedEurPlan.planType,
    };

    // Update tempData in context - this will propagate to all other components
    setTempData(enhancedTempData);

    // Select the plan with converted currency
    setSelectedPlanId(planKey);

    // Update context with the plan (keeping original plan structure but will display converted price)
    setSelectedPlan(selectedEurPlan);
    setContextPlanId(planKey);

    // Close modal and reset state
    setIsForexModalOpen(false);
    setSelectedEurPlan(null);
  };

  // Handle forex modal close
  const handleForexModalClose = () => {
    setIsForexModalOpen(false);
    setSelectedEurPlan(null);
  };

  // Helper function to get display price (original or converted)
  const getDisplayPrice = (plan: (typeof planOptions)[0]) => {
    const planKey = plan.productId.toString();
    const convertedPlan = convertedPlans.get(planKey);

    if (convertedPlan) {
      return `${convertedPlan.currency} ${formatWithCommas(convertedPlan.amount)}`;
    }

    return `${plan.rate.currency.currencyCode} ${formatWithCommas(plan.rate.rateAmount)}`;
  };

  // Format destination array for display
  const formatDestination = (destinations: string[]) => {
    if (destinations.length === 0) return "";
    if (destinations.length === 1) return destinations[0];
    return destinations.join(", ");
  };

  // Generate complete itinerary string with destination codes
  const generateCompleteItinerary = () => {
    if (travelData.tripType === "Annual Multi-Trip") {
      return "Annual Multi-Trip Coverage";
    }

    const startCode =
      internationalDestinationMapping[travelData.startTrip] ||
      provinceMapping[travelData.startTrip] ||
      travelData.startTrip;

    // For international destinations, use international destination mapping
    // For domestic destinations, use regular destination mapping
    const destinationCodes = travelData.destination.map((dest) => {
      // First try international destination mapping
      if (internationalDestinationMapping[dest]) {
        return internationalDestinationMapping[dest];
      }
      // Fallback to regular destination mapping (for domestic)
      return destinationMapping[dest] || dest;
    });

    const endTripMapping = Object.assign(
      {},
      internationalDestinationMapping,
      provinceMapping,
    );

    const endCode = endTripMapping[travelData.endTrip] || travelData.endTrip;

    // Build itinerary with codes
    let itinerary = startCode;
    if (destinationCodes.length > 0) {
      itinerary += `-${destinationCodes.join("-")}`;
    }
    if (endCode) {
      if (
        destinationCodes.lastIndexOf(endCode) !==
        destinationCodes.length - 1
      )
        itinerary += `-${endCode}`;
    }

    return itinerary;
  };

  // Format travel dates for display
  const formatTravelDates = () => {
    if (travelData.tripType === "Annual Multi-Trip") {
      return (
        format(travelData.travelDate, "MMM dd, yyyy") +
        " - " +
        format(addDays(new Date(travelData.travelDate), 364), "MMM dd, yyyy")
      );
    }

    if (travelData.startDate && travelData.endDate) {
      return dateUtils.formatTravelDateRange(
        travelData.startDate,
        travelData.endDate,
      );
    }

    return "";
  };

  // Calculate days of travel
  const calculateDaysOfTravel = () => {
    if (travelData.tripType === "Annual Multi-Trip") {
      return 365; // Annual coverage
    }

    if (travelData.startDate && travelData.endDate) {
      const start = new Date(travelData.startDate);
      const end = new Date(travelData.endDate);
      const diffTime = Math.abs(end.getTime() - start.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // Include both start and end dates
      return diffDays;
    }

    return 0;
  };

  // Get medical coverage from benefits
  const getMedicalCoverage = (plan: (typeof planOptions)[0]): string => {
    const medicalBenefit = plan.benefitsCategories.find(
      (b) => b.benefitsCategory === "Medical Treatment",
    );
    if (medicalBenefit && medicalBenefit.categoryCoverage) {
      return `Medical Treatment Benefit of up to ${plan.rate.currency.currencyCode} ${medicalBenefit.categoryCoverage}`;
    }
    return `Medical Treatment Benefit of up to ${plan.rate.currency.currencyCode} ${plan.maxBenefitLimit.toLocaleString()}`;
  };

  // Get travel assistance info
  const getTravelAssistance = (plan: (typeof planOptions)[0]): string => {
    const assistanceBenefit = plan.benefitsCategories.find(
      (b) => b.benefitsCategory === "Emergency Assistance Benefits",
    );

    if (assistanceBenefit) {
      // Check if it has multiple assistance services
      const assistanceServices = assistanceBenefit.benefits.length;
      if (assistanceServices >= 3) {
        return "Premium travel assistance";
      } else if (assistanceServices >= 2) {
        return "Standard travel assistance";
      }
    }
    return "Core travel assistance";
  };

  // Get plan features from productOverview with fallback to static features
  const getPlanFeatures = (plan: (typeof planOptions)[0]): string[] => {
    // Check if productOverview exists and has descriptions
    if (plan.productOverview && plan.productOverview.length > 0) {
      // Use productOverview descriptions
      return plan.productOverview.map((overview) => overview.description);
    }

    // Fallback to static features if productOverview is not available
    const currentSection =
      planSelection?.section?.toLowerCase().trim() || "domestic";

    switch (currentSection) {
      case "domestic":
        return [
          getMedicalCoverage(plan), // Keep first bullet dynamic for medical coverage
          "Protection for medical emergencies and travel inconveniences",
          "Coverage for travel inconveniences such as flight delay, baggage delay, and emergency assistance among others",
        ];

      case "shortsecure":
      case "short secure":
        return [
          getMedicalCoverage(plan), // Keep first bullet dynamic for medical coverage
          "With Personal Accident Coverage for accidental bodily injury",
          getTravelAssistance(plan), // Keep third bullet dynamic for travel assistance
        ];

      case "asia and oceania":
        return [
          getMedicalCoverage(plan), // Keep first bullet dynamic for medical coverage
          "Online travel insurance anytime, anywhere",
          "High-value protection at affordable rates",
          "Individual, Family and Group coverage with Single and Multi-Trip plan options",
        ];

      case "worldwideelite":
      case "worldwide elite":
        return [
          getMedicalCoverage(plan), // Keep first bullet dynamic for medical coverage
          "Travel insurance tailored for European and U.S. trips, includes Schengen visa-approved coverage",
          "High-value protection at affordable rates",
          "24/7 worldwide emergency assistance for unexpected travel issues and emergencies",
        ];

      default:
        // Fallback to domestic
        return [
          getMedicalCoverage(plan),
          "Protection for medical emergencies and travel inconveniences",
          "Coverage for travel inconveniences such as flight delay, baggage delay, and emergency assistance among others",
        ];
    }
  };

  const daysOfTravel = calculateDaysOfTravel();
  const completeItinerary = generateCompleteItinerary();
  const formattedTravelDates = formatTravelDates();

  return (
    <div className="travel-details-container">
      <div className="travel-details-background" />
      <div className="travel-details-overlay"></div>
      <div className="travel-details-content">
        {/* My Travel Details Section */}
        <div className="travel-details-card">
          <h2 className="travel-details-card-title">Your Travel Details:</h2>

          {travelData.tripType === "Annual Multi-Trip" ? (
            /* Annual Multi-Trip: Display only specific data in 2 rows */
            <div className="travel-details-rows">
              {/* Row 1 */}
              <div className="travel-details-row">
                <div className="travel-detail-pair">
                  <span className="travel-detail-label">Type of Trip</span>
                  <span className="travel-detail-value">Annual</span>
                </div>
                <div className="travel-detail-pair">
                  <span className="travel-detail-label">Plan Type</span>
                  <span className="travel-detail-value">Multi-trip</span>
                </div>
              </div>

              {/* Row 2 */}
              <div className="travel-details-row">
                <div className="travel-detail-pair">
                  <span className="travel-detail-label">Purpose of Travel</span>
                  <span className="travel-detail-value">
                    {travelData.purposeOfTravel}
                  </span>
                </div>
                <div className="travel-detail-pair">
                  <span className="travel-detail-label">Travel Dates</span>
                  <span className="travel-detail-value">
                    {formattedTravelDates}
                    <br />
                    <span className="travel-days">
                      {daysOfTravel} Days of travel
                    </span>
                  </span>
                </div>
              </div>
            </div>
          ) : (
            /* Single Trip: Display all data in 4 rows */
            <div className="travel-details-rows">
              {/* Row 1 */}
              <div className="travel-details-row">
                {formData.category !== TripCategory.Inbound ? (
                  <div className="travel-detail-pair">
                    <span className="travel-detail-label">
                      {getDsName() === "short_secure"
                        ? "Mode of Travel"
                        : "Type of Trip"}
                    </span>
                    <span className="travel-detail-value">
                      {getDsName() === "short_secure" &&
                      travelData.transportMode
                        ? `Via Land or Sea`
                        : travelData.tripType}
                    </span>
                  </div>
                ) : (
                  <div className="travel-detail-pair">
                    <span className="travel-detail-label">
                      Country of Origin
                    </span>
                    <span className="travel-detail-value">
                      {travelData.startTrip}
                    </span>
                  </div>
                )}
                <div className="travel-detail-pair">
                  <span className="travel-detail-label">Plan Type</span>
                  <span className="travel-detail-value">
                    {travelData.planType}
                  </span>
                </div>
              </div>

              {/* Row 2 */}
              <div className="travel-details-row">
                {formData.category !== TripCategory.Inbound ? (
                  <div className="travel-detail-pair">
                    <span className="travel-detail-label">Start Trip</span>
                    <span className="travel-detail-value">
                      {travelData.startTrip}
                    </span>
                  </div>
                ) : (
                  <div className="travel-detail-pair">
                    <span className="travel-detail-label">Destination</span>
                    <span className="travel-detail-value">
                      {travelData.destination}
                    </span>
                  </div>
                )}
                <div className="travel-detail-pair">
                  <span className="travel-detail-label">Purpose of Travel</span>
                  <span className="travel-detail-value">
                    {travelData.purposeOfTravel}
                  </span>
                </div>
              </div>

              {/* Row 3 */}
              <div className="travel-details-row">
                {formData.category !== TripCategory.Inbound ? (
                  <div className="travel-detail-pair">
                    <span className="travel-detail-label">Destination</span>
                    <span className="travel-detail-value">
                      {formatDestination(travelData.destination)}
                    </span>
                  </div>
                ) : travelData.philippinesEndpoint === "Yes" ? (
                  <div className="travel-detail-pair">
                    <span className="travel-detail-label">Return Country</span>
                    <span className="travel-detail-value">
                      {travelData.startTrip}
                    </span>
                  </div>
                ) : (
                  <div className="travel-detail-pair">
                    <span className="travel-detail-label">Travel Dates</span>
                    <span className="travel-detail-value">
                      {formattedTravelDates}
                    </span>
                  </div>
                )}
                {formData.category !== TripCategory.Inbound ? (
                  <div className="travel-detail-pair">
                    <span className="travel-detail-label">
                      Your Complete Itinerary
                    </span>
                    <span className="travel-detail-value">
                      {completeItinerary}
                    </span>
                  </div>
                ) : travelData.philippinesEndpoint === "Yes" ? (
                  <div className="travel-detail-pair">
                    <span className="travel-detail-label">
                      Your Complete Itinerary
                    </span>
                    <span className="travel-detail-value">
                      {completeItinerary}
                    </span>
                  </div>
                ) : null}
              </div>

              {/* Row 4 - Inbound Travel Dates (only when Yes, since No already shows it in Row 3) */}
              {formData.category === TripCategory.Inbound &&
                travelData.philippinesEndpoint === "Yes" && (
                <div className="travel-details-row">
                  <div className="travel-detail-pair">
                    <span className="travel-detail-label">Travel Dates</span>
                    <span className="travel-detail-value">
                      {formattedTravelDates}
                    </span>
                  </div>
                </div>
              )}

              {/* Row 4 */}
              {formData.category !== TripCategory.Inbound && (
                <div className="travel-details-row">
                  <div className="travel-detail-pair">
                    {travelData.endTrip && (
                      <>
                        <span className="travel-detail-label">End Trip</span>
                        <span className="travel-detail-value">
                          {travelData.endTrip}
                        </span>
                      </>
                    )}
                  </div>
                  <div className="travel-detail-pair">
                    <span className="travel-detail-label">Travel Dates</span>
                    <span className="travel-detail-value">
                      {formattedTravelDates}
                    </span>
                  </div>
                </div>
              )}
              {/* Row 5 */}
              <div className="travel-details-row travel-details-row-days-of-travel">
                {formData.category !== TripCategory.Inbound && (
                  <div className="travel-detail-pair travel-detail-pair-blank"></div>
                )}
                <div className="travel-detail-pair travel-detail-pair-days-of-travel">
                  <span className="travel-detail-label"></span>
                  <span className="travel-days">
                    {daysOfTravel} Days of travel
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Plan Types Section */}
        <div className="plan-types-section">
          <h2 className="plan-types-title">
            Recommended Travelsafe Plans For You:
          </h2>

          {loading && (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Loading available plans...</p>
            </div>
          )}

          {error && (
            <div className="error-state">
              <div className="error-icon">⚠️</div>
              <p>Error loading plans: {error}</p>
              <button onClick={handleRetryFetchPlans} className="retry-button">
                Retry
              </button>
            </div>
          )}

          {!loading && !error && sortedPlanOptions.length === 0 && (
            <div className="no-plans-state">
              <div className="no-plans-icon">📋</div>
              <p>No plans available for the selected criteria.</p>
              <p className="no-plans-subtitle">
                Please try adjusting your travel details.
              </p>
            </div>
          )}

          {!loading && !error && sortedPlanOptions.length > 0 && (
            <div className="plan-options-grid">
              {sortedPlanOptions.map((plan) => {
                const isSelected = selectedPlanId === plan.productId.toString();

                return (
                  <div
                    key={plan.productId}
                    className={`plan-option-card ${isSelected ? "selected" : ""}`}
                    onClick={() => handlePlanSelect(plan.productId.toString())}
                  >
                    <div
                      className={`plan-selection-indicator ${isSelected ? "selected" : "unselected"}`}
                    >
                      {isSelected ? (
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                        >
                          <circle cx="10" cy="10" r="10" fill="#f59e0b" />
                          <path
                            d="M6 10l3 3 5-6"
                            stroke="white"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      ) : (
                        <div className="empty-circle"></div>
                      )}
                    </div>

                    <div className="plan-price">{getDisplayPrice(plan)}</div>

                    <div className="plan-name">{plan.productName}</div>
                    <div className="plan-type">{plan.planType}</div>

                    <div className="plan-features">
                      {getPlanFeatures(plan).map((feature, index) => (
                        <div key={index} className="plan-feature">
                          <span className="feature-bullet">•</span>
                          <span className="feature-text">{feature}</span>
                        </div>
                      ))}
                    </div>

                    <button
                      className="view-benefits-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewFullBenefits(plan.productId.toString());
                      }}
                    >
                      View Full Benefits
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="plan-navigation-actions">
            <button type="button" className="back-button" onClick={onBack}>
              BACK
            </button>

            <button
              type="button"
              className={`next-button ${!isNextButtonEnabled ? "disabled" : ""}`}
              onClick={handleNext}
              disabled={!isNextButtonEnabled}
            >
              NEXT
            </button>
          </div>
        </div>
      </div>

      {/* Benefits Modal */}
      <BenefitsModal
        isOpen={isBenefitsModalOpen}
        onClose={handleCloseBenefitsModal}
        planId={selectedBenefitsPlanId}
        planOptions={planOptions}
      />

      {/* Forex Rate Conversion Modal */}
      <ForexRateConversion
        isOpen={isForexModalOpen}
        onClose={handleForexModalClose}
        currentCurrency={selectedEurPlan?.rate.currency.currencyCode || "EUR"}
        currentCurrencyId={selectedEurPlan?.rate.currency.currencyId ?? 0}
        amount={selectedEurPlan?.rate.rateAmount || 0}
        onConversionComplete={handleForexConversionComplete}
        previousConversion={
          selectedEurPlan
            ? convertedPlans.get(selectedEurPlan.productId.toString()) || null
            : null
        }
        isInbound={formData.category === TripCategory.Inbound}
      />
    </div>
  );
};

export default TravelDetailsSection;
